import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { verifyCertificate } from "../helper/certificate_helper";

export default function VerifyCertificatePage() {
    const params = useParams();
    const certificateNumber =
        params?.certNumber || params?.cert || params?.certificateNumber || "";

    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!certificateNumber) {
            setError("No certificate number provided in the URL.");
            return;
        }

        let mounted = true;

        (async () => {
            try {
                setLoading(true);
                setError("");
                setResult(null);

                const data = await verifyCertificate(certificateNumber);
                if (!mounted) return;

                if (data?.success) setResult(data.result || {});
                else {
                    const friendly =
                        data?.message === "CERTIFICATE_NOT_EXIST"
                            ? "Certificate not found. Please check the number and try again."
                            : data?.message || "Certificate not found or invalid.";
                    setError(friendly);
                }
            } catch (err) {
                console.error("verify error", err);
                toast.error("Something went wrong while verifying.");
                setError("Something went wrong while verifying. Try again later.");
            } finally {
                if (mounted) setLoading(false);
            }
        })();

        return () => {
            mounted = false;
        };
    }, [certificateNumber]);

    const formatDate = (arr) => {
        if (!arr || arr.length < 3) return "-";
        try {
            const d = new Date(arr[0], arr[1] - 1, arr[2]);
            return d.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            });
        } catch {
            return "-";
        }
    };

    return (
        <div className="vc-root">
            <header className="vc-header">
                <div className="vc-header-inner">
                    <div className="vc-logo">
                        {certificateNumber.includes("TML") ? "TML" : "ASVP"}
                    </div>
                    <h1 className="vc-title">Certificate Verification</h1>
                </div>
            </header>

            <main className="vc-main">
                <section className="vc-card">
                    <aside className="vc-left">
                        <div className="vc-deco">
                            <svg
                                width="80"
                                height="80"
                                viewBox="0 0 24 24"
                                fill="none"
                                aria-hidden
                            >
                                <path
                                    d="M12 2L15 8L22 9L17 14L18 21L12 18L6 21L7 14L2 9L9 8L12 2Z"
                                    stroke="currentColor"
                                    strokeWidth="0.5"
                                />
                            </svg>
                        </div>

                        <div className="vc-certno">
                            {certificateNumber || "No Certificate"}
                        </div>

                        <div
                            className={`vc-badge ${result
                                ? "vc-badge--ok"
                                : error
                                    ? "vc-badge--fail"
                                    : "vc-badge--pending"
                                }`}
                        >
                            {loading
                                ? "Verifying..."
                                : result
                                    ? "Verified"
                                    : error
                                        ? "Not Found"
                                        : "Pending"}
                        </div>
                    </aside>

                    <div className="vc-right">
                        {loading ? (
                            <div className="vc-spinner-wrap">
                                <div className="spinner" aria-hidden></div>
                                <div className="spinner-text">
                                    Checking certificate — please wait
                                </div>
                            </div>
                        ) : result ? (
                            <div className="vc-success">
                                <h2 className="vc-success-title">✅ Certificate Verified</h2>
                                <p className="vc-success-sub">
                                    Details retrieved from the verification server.
                                </p>

                                <div className="vc-details">
                                    <Row label="Student Name" value={result.studentName} />
                                    <Row label="Depot" value={result.depot} />
                                  
                                    <Row label="City" value={result.city || "-"} />
                                    <Row
                                        label="Training Period"
                                        value={`${formatDate(result.fromDate)} — ${formatDate(
                                            result.toDate
                                        )}`}
                                    />
                                    <Row
                                        label="Validity"
                                        value={formatDate(result.validityDate)}
                                    />
                                    <Row
                                        label="Certificate No"
                                        value={result.certificateNo}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="vc-error">
                                <h2 className="vc-error-title">❌ Certificate Not Found</h2>
                                <p className="vc-error-sub">
                                    {error || "We could not find this certificate number."}
                                </p>
                                <div className="vc-hint">
                                    Tip: Make sure the number is correct and try again.
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            </main>

            <style>{`
                :root {
                    --radius: 18px;
                    --muted: #94a3b8;
                    --bg-dark: #0f172a;
                    --glass: rgba(255,255,255,0.1);
                }
                * { box-sizing: border-box; }
                body, html, .vc-root { margin: 0; padding: 0; }

                .vc-root {
                    min-height: 100vh;
                    display: flex;
                    flex-direction: column;
                    font-family: "Inter", sans-serif;
                    background: linear-gradient(135deg, #0f172a, #1e293b, #0b1220);
                    color: #fff;
                }
                .vc-header {
                    padding: 16px 20px;
                    border-bottom: 1px solid rgba(255,255,255,0.05);
                }
                .vc-header-inner {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .vc-logo {
                    width: 44px;
                    height: 44px;
                    background: linear-gradient(135deg, #6366f1, #06b6d4);
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                }
                .vc-title {
                    font-size: 1.1rem;
                    font-weight: 600;
                }
                .vc-main {
                    flex: 1;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    padding: 20px;
                }
                .vc-card {
                    width: 100%;
                    max-width: 900px;
                    background: rgba(255,255,255,0.08);
                    backdrop-filter: blur(14px);
                    border-radius: var(--radius);
                    box-shadow: 0 8px 24px rgba(0,0,0,0.4);
                    display: grid;
                    grid-template-columns: 280px 1fr;
                    overflow: hidden;
                }
                .vc-left {
                    background: rgba(255,255,255,0.06);
                    padding: 20px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 16px;
                }
                .vc-deco {
                    width: 100px;
                    height: 100px;
                    border-radius: 14px;
                    background: linear-gradient(135deg, #3b82f6, #8b5cf6);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: inset 0 -6px 18px rgba(255,255,255,0.06);
                }
                .vc-certno {
                    font-weight: 700;
                    font-size: 0.95rem;
                    background: rgba(255,255,255,0.08);
                    padding: 8px 14px;
                    border-radius: 999px;
                }
                .vc-badge {
                    padding: 8px 14px;
                    border-radius: 999px;
                    font-weight: 600;
                }
                .vc-badge--ok {
                    background: linear-gradient(90deg, #10b981, #34d399);
                    color: #042c1e;
                }
                .vc-badge--fail {
                    background: linear-gradient(90deg, #f97316, #fb7185);
                    color: #3b0214;
                }
                .vc-badge--pending {
                    background: rgba(255,255,255,0.12);
                }

                .vc-right {
                    padding: 20px;
                }

                .vc-details {
                    margin-top: 16px;
                    background: rgba(0,0,0,0.15);
                    border-radius: 10px;
                    padding: 12px;
                }

                .vc-row {
                    display: flex;
                    justify-content: space-between;
                    padding: 8px 6px;
                    border-bottom: 1px solid rgba(255,255,255,0.08);
                }

                .vc-row:last-child {
                    border-bottom: none;
                }

                .vc-label {
                    color: #cbd5e1;
                }
                .vc-value {
                    font-weight: 600;
                }

                /* Responsive */
                @media (max-width: 768px) {
                    .vc-card {
                        grid-template-columns: 1fr;
                        text-align: center;
                    }
                    .vc-left {
                        flex-direction: column;
                        padding: 24px 16px;
                    }
                    .vc-right {
                        padding: 16px;
                    }
                    .vc-deco {
                        width: 80px;
                        height: 80px;
                    }
                    .vc-certno {
                        font-size: 0.9rem;
                    }
                    .vc-row {
                        flex-direction: column;
                        align-items: center;
                        padding: 10px 0;
                    }
                    .vc-label {
                        font-size: 0.85rem;
                    }
                    .vc-value {
                        font-size: 1rem;
                    }
                }
            `}</style>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="vc-row">
            <div className="vc-label">{label}</div>
            <div className="vc-value">{value || "-"}</div>
        </div>
    );
}
